/**
 * Éditeur du grade CCQ d'un employé (Phase 3 — Module CCQ 2026-05-06).
 * S'affiche dans l'onglet Salaires de la fiche employé.
 *
 * Quand un grade est sélectionné, le composant affiche aussi le taux horaire
 * et le coût employeur correspondants tirés de la grille CCQ active —
 * pour info uniquement (pas modifiable ici, géré sur /reglages/ccq).
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Save, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GRADE_OPTIONS = [
  { value: '__none__', label: 'Aucun (pas accrédité CCQ)' },
  { value: 'apprenti_1', label: 'Apprenti — Période 1' },
  { value: 'apprenti_2', label: 'Apprenti — Période 2' },
  { value: 'apprenti_3', label: 'Apprenti — Période 3' },
  { value: 'compagnon', label: 'Compagnon' },
];

export function GradeCCQEditor({ employeeId, currentGrade, onSaved }) {
  const [grade, setGrade] = useState(currentGrade || '__none__');
  const [activeGrille, setActiveGrille] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setGrade(currentGrade || '__none__');
    setDirty(false);
  }, [currentGrade, employeeId]);

  useEffect(() => {
    axios.get(`${API_URL}/api/ccq/grille/active`)
      .then(r => setActiveGrille(r.data))
      .catch(() => setActiveGrille(null));
  }, []);

  const tauxInfo = (() => {
    if (!activeGrille || grade === '__none__' || !activeGrille.grades?.[grade]) return null;
    const g = activeGrille.grades[grade];
    return {
      taux: g.taux_salaire,
      brut: g.salaire_brut,
      total_mo: g.total_cout_main_oeuvre,
    };
  })();

  const save = async () => {
    setSaving(true);
    try {
      const payload = grade === '__none__' ? { grade_ccq: null } : { grade_ccq: grade };
      await axios.put(`${API_URL}/api/employees/${employeeId}`, payload);
      toast.success('Grade CCQ mis à jour');
      setDirty(false);
      onSaved?.(payload.grade_ccq);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 p-4 bg-violet-50/40 border border-violet-200 rounded-lg" data-testid="grade-ccq-editor">
      <div className="flex items-start gap-3 mb-3">
        <FileSpreadsheet className="w-5 h-5 text-violet-600 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-semibold text-sm text-violet-900">Grade CCQ — Peintres</h4>
          <p className="text-xs text-violet-700 mt-0.5">
            Si l'employé travaille parfois sur des chantiers <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100 align-middle">CCQ</Badge>,
            sélectionnez son grade. Sinon, laissez « Aucun ».
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <Label htmlFor="grade-ccq-select" className="text-xs">Grade actuel</Label>
          <Select value={grade} onValueChange={(v) => { setGrade(v); setDirty(true); }}>
            <SelectTrigger id="grade-ccq-select" data-testid="select-grade-ccq">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GRADE_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value} data-testid={`grade-ccq-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button
            onClick={save}
            disabled={!dirty || saving}
            className="w-full bg-violet-600 hover:bg-violet-700"
            data-testid="btn-save-grade-ccq"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? 'Sauvegarde…' : 'Sauvegarder'}
          </Button>
        </div>
      </div>

      {tauxInfo && (
        <div className="mt-3 p-3 bg-white rounded border border-violet-100 text-xs">
          <p className="text-violet-700 font-medium mb-1">
            Taux applicables (grille active du {activeGrille?.date_effet}) :
          </p>
          <div className="grid grid-cols-3 gap-2 text-slate-700">
            <div>
              <span className="text-slate-500">Taux salaire</span>
              <div className="font-semibold tabular-nums" data-testid="info-taux-salaire">
                {tauxInfo.taux?.toFixed(2)} $/h
              </div>
            </div>
            <div>
              <span className="text-slate-500">Salaire brut (employé)</span>
              <div className="font-semibold tabular-nums" data-testid="info-salaire-brut">
                {tauxInfo.brut?.toFixed(2)} $/h
              </div>
            </div>
            <div>
              <span className="text-slate-500">Coût main d'œuvre</span>
              <div className="font-semibold tabular-nums text-violet-700" data-testid="info-cout-mo">
                {tauxInfo.total_mo?.toFixed(2)} $/h
              </div>
            </div>
          </div>
        </div>
      )}

      {grade === '__none__' && (
        <p className="text-xs text-slate-500 italic mt-2">
          ℹ️ Si cet employé punche sur un BT marqué CCQ sans grade défini, le punch sera
          enregistré mais signalé en rouge dans Validation des heures pour correction.
        </p>
      )}
    </div>
  );
}
